import { useState, useEffect, type FormEvent } from 'react';
import { MODELS } from '../lib/models';
import { X } from 'lucide-react';
import type { Agent } from '../hooks/useAgents';
import type { AgentGroup } from '../types/agent-group';

const MAX_PROMPT = 100 * 1024;

type FormTab = 'basic' | 'prompt' | 'advanced';

export interface AgentFormData {
  name: string;
  role: Agent['role'];
  scope: Agent['scope'];
  systemPrompt: string;
  groupId?: string | null;
  defaultModel?: string | null;
  defaultCwd?: string | null;
  maxTurnsDefault?: number | null;
  maxBudgetDefault?: number | null;
  allowedTools?: string[] | null;
  autoMcpServers?: string[] | null;
}

interface AgentFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: AgentFormData) => Promise<void>;
  agent?: Agent | null;
  groups?: AgentGroup[];
  isPending?: boolean;
}

export default function AgentForm({ open, onClose, onSubmit, agent, groups, isPending }: AgentFormProps) {
  const [tab, setTab] = useState<FormTab>('basic');
  const [name, setName] = useState('');
  const [role, setRole] = useState<Agent['role']>('executor');
  const [scope, setScope] = useState<Agent['scope']>('project');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [groupId, setGroupId] = useState<string>('');
  const [defaultModel, setDefaultModel] = useState('');
  const [defaultCwd, setDefaultCwd] = useState('');
  const [maxTurns, setMaxTurns] = useState('');
  const [maxBudget, setMaxBudget] = useState('');
  const [toolsText, setToolsText] = useState('');

  useEffect(() => {
    if (agent) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setName(agent.name);
      setRole(agent.role);
      setScope(agent.scope || 'project');
      setSystemPrompt(agent.systemPrompt ?? '');
      setGroupId(agent.groupId ?? '');
      setDefaultModel(agent.defaultModel ?? '');
      setDefaultCwd(agent.defaultCwd ?? '');
      setMaxTurns(agent.maxTurnsDefault?.toString() ?? '');
      setMaxBudget(agent.maxBudgetDefault?.toString() ?? '');
      setToolsText(agent.allowedTools?.join(', ') ?? '');
    } else {
      setName('');
      setRole('executor');
      setScope('project');
      setSystemPrompt('');
      setGroupId('');
      setDefaultModel('');
      setDefaultCwd('');
      setMaxTurns('');
      setMaxBudget('');
      setToolsText('');
    }
    setTab('basic');
  }, [agent, open]);

  if (!open) return null;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const data: AgentFormData = {
      name,
      role,
      scope,
      systemPrompt,
      groupId: groupId || null,
      defaultModel: defaultModel || null,
      defaultCwd: defaultCwd || null,
      maxTurnsDefault: maxTurns ? Number(maxTurns) : null,
      maxBudgetDefault: maxBudget ? Number(maxBudget) : null,
      allowedTools: toolsText ? toolsText.split(',').map(t => t.trim()).filter(Boolean) : null,
    };
    await onSubmit(data);
    onClose();
  };

  const TABS: { key: FormTab; label: string }[] = [
    { key: 'basic', label: 'Basic Info' },
    { key: 'prompt', label: 'System Prompt' },
    { key: 'advanced', label: 'Advanced' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-2xl rounded-xl border border-slate-200 bg-white shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-slate-900">
            {agent ? 'Edit Agent' : 'Create Agent'}
          </h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-slate-200 px-6">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                tab === t.key
                  ? 'border-slate-900 text-slate-900'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6" style={{ minHeight: '320px' }}>
            {/* Basic Info Tab */}
            {tab === 'basic' && (
              <div className="space-y-4">
                <div>
                  <label htmlFor="agent-name" className="mb-1.5 block text-sm font-medium text-slate-700">Name</label>
                  <input
                    id="agent-name"
                    type="text"
                    required
                    value={name}
                    onChange={e => setName(e.target.value)}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
                    placeholder="Agent name"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="agent-role" className="mb-1.5 block text-sm font-medium text-slate-700">Role</label>
                    <select
                      id="agent-role"
                      value={role}
                      onChange={e => setRole(e.target.value as Agent['role'])}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-slate-500 focus:outline-none"
                    >
                      <option value="creator">Creator</option>
                      <option value="executor">Executor</option>
                      <option value="researcher">Researcher</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="agent-scope" className="mb-1.5 block text-sm font-medium text-slate-700">Scope</label>
                    <select
                      id="agent-scope"
                      value={scope}
                      onChange={e => setScope(e.target.value as Agent['scope'])}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-slate-500 focus:outline-none"
                    >
                      <option value="project">Project</option>
                      <option value="global">Global</option>
                    </select>
                  </div>
                </div>
                {groups && groups.length > 0 && (
                  <div>
                    <label htmlFor="agent-group" className="mb-1.5 block text-sm font-medium text-slate-700">Group</label>
                    <select
                      id="agent-group"
                      value={groupId}
                      onChange={e => setGroupId(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-slate-500 focus:outline-none"
                    >
                      <option value="">No group</option>
                      {groups.map(g => (
                        <option key={g.id} value={g.id}>{g.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}

            {/* System Prompt Tab */}
            {tab === 'prompt' && (
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <label htmlFor="agent-prompt" className="block text-sm font-medium text-slate-700">System Prompt</label>
                  <span className={`text-xs ${systemPrompt.length > MAX_PROMPT ? 'text-red-500' : 'text-slate-400'}`}>
                    {systemPrompt.length.toLocaleString()} / {MAX_PROMPT.toLocaleString()}
                  </span>
                </div>
                <textarea
                  id="agent-prompt"
                  rows={14}
                  value={systemPrompt}
                  onChange={e => setSystemPrompt(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm text-slate-900 placeholder-slate-400 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
                  placeholder="Enter the system prompt for this agent..."
                />
              </div>
            )}

            {/* Advanced Tab */}
            {tab === 'advanced' && (
              <div className="space-y-4">
                <div>
                  <label htmlFor="agent-model" className="mb-1.5 block text-sm font-medium text-slate-700">Default Model</label>
                  <select
                    id="agent-model"
                    value={defaultModel}
                    onChange={e => setDefaultModel(e.target.value)}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-slate-500 focus:outline-none"
                  >
                    <option value="">System default</option>
                    {MODELS.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="agent-turns" className="mb-1.5 block text-sm font-medium text-slate-700">Max Turns</label>
                    <input
                      id="agent-turns"
                      type="number"
                      min={1}
                      value={maxTurns}
                      onChange={e => setMaxTurns(e.target.value)}
                      placeholder="50"
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-slate-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label htmlFor="agent-budget" className="mb-1.5 block text-sm font-medium text-slate-700">Max Budget ($)</label>
                    <input
                      id="agent-budget"
                      type="number"
                      min={0}
                      step={0.5}
                      value={maxBudget}
                      onChange={e => setMaxBudget(e.target.value)}
                      placeholder="5.00"
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-slate-500 focus:outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="agent-cwd" className="mb-1.5 block text-sm font-medium text-slate-700">Working Directory</label>
                  <input
                    id="agent-cwd"
                    type="text"
                    value={defaultCwd}
                    onChange={e => setDefaultCwd(e.target.value)}
                    placeholder="/path/to/project"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-slate-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="agent-tools" className="mb-1.5 block text-sm font-medium text-slate-700">Allowed Tools</label>
                  <input
                    id="agent-tools"
                    type="text"
                    value={toolsText}
                    onChange={e => setToolsText(e.target.value)}
                    placeholder="Bash, Read, Write, Edit, Glob, Grep (comma-separated)"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-slate-500 focus:outline-none"
                  />
                  <p className="mt-1 text-xs text-slate-400">Leave empty to allow all tools</p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 border-t border-slate-200 px-6 py-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending || !name.trim() || systemPrompt.length > MAX_PROMPT}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
            >
              {isPending ? 'Saving...' : agent ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
